import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/styles'
import {
    Avatar,
    ButtonBase,
    Grid,
    Stack,
    Typography
} from '@material-ui/core'
import NoEncryptionIcon from '@material-ui/icons/NoEncryption'
import { ReactComponent as ItsMeLogo } from '../../assets/images/svg/itsme-logo.svg'
import { ReactComponent as GoogleLogo } from '../../assets/images/svg/google-logo.svg'
import { CheckingLogin, LogoContainer } from '../Atoms'
import { ButtonCollection } from '../Molecules'
import { startLoginFlow, parseRedirectToken, userSubject } from '../../utils/Authentication'
import { getParams, getUrl, goToUrl, stringifyUrl } from '../../utils/UrlManipulation'
import { deleteCookie, readCookie, storeCookie } from '../../utils/LocalStorage'
import { useAsyncError } from '../../utils/Error'

const useStyles = makeStyles(theme => ({
    avatar: {
        width: 150,
        height: 150,
        backgroundColor: theme.palette.primary.main,
    },
    icon: {
        width: 90,
        height: 90,
    },
    main: {
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
    content: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        rowGap: "40px",
    },
    auth0Button: {
        backgroundColor: "#FF4612",
        color: "white",
        borderRadius: 12,
        border: '1px solid black',
        width: 280,
        height: 56,
        textTransform: 'none',
        marginTop: 15
    },
    googleButton: {
        backgroundColor: "#F1F1F1",
        color: "black",
        borderRadius: 5,
        border: '1px solid black',
        width: 280,
        height: 56,
        textTransform: 'none',
        marginTop: 15
    }
}))

const Login = ({ providerType }) => {
    const classes = useStyles()
    const { t } = useTranslation()
    const [loading, setLoading] = React.useState(true)
    const [loginError, setLoginError] = React.useState(null)
    const throwError = useAsyncError()
    const redirectCookieName = 'redirectTargetAfterLogin'
    const { autologin, error, ...remainingParams } = getParams()

    const logIn = () => {
        const currentPath = getUrl()
        if (currentPath !== '/login' && currentPath !== '/login/dev') {
            const redirectCookieExpirationDate = new Date()
            redirectCookieExpirationDate.setMinutes(redirectCookieExpirationDate.getMinutes() + 5)
            const originalUrl = stringifyUrl(currentPath, remainingParams)
            storeCookie(redirectCookieName, originalUrl, redirectCookieExpirationDate)
        }
        // Check if query parameter 'code' or 'error' is present
        // If so, we are being redirected after being logged in at the IDP
        // so we don't need to start the login flow (again)
        const params = getParams()
        if (params.code || params.error) {
            return;
        }
        startLoginFlow(providerType)
    }

    const onUserHandler = (user) => {
        if (user && window.location.pathname.includes("login")) {
            const originalUrl = readCookie(redirectCookieName)
            deleteCookie(redirectCookieName)
            goToUrl(originalUrl || '/')
        }
    }

    React.useEffect(() => {
        userSubject.attach(onUserHandler)
        return () => userSubject.detach(onUserHandler)
    }, [providerType])

    React.useEffect(() => {
        let isSubscribed = true
        const parsingPromise = parseRedirectToken(
            providerType,
            window.location.origin + window.location.pathname
        )

        if (parsingPromise) {
            parsingPromise
                .catch(error => {
                    if (isSubscribed) {
                        setLoginError(error)
                        setLoading(false)
                    }
                })
        } else {
            setLoading(false)
        }
        return () => isSubscribed = false
    }, [providerType])

    if (error === 'access_denied') throwError({
        status: '403',
        title: 'Access Denied',
        detail: 'You do not have access to this application',
    })

    logIn()

    if (loading || autologin) return <CheckingLogin error={loginError} />

    const isLoginPage = window.location.pathname.includes("login")

    return (
        <LogoContainer style={{ overflowY: "auto !important" }}>
            <Stack direction="row" justifyContent='center' alignItems='center' style={{ height: "100vh" }}>
                <div style={{ position: "absolute", right: 48, top: 0, zIndex: 100 }} >
                    <ButtonCollection />
                </div>
                <div className={classes.main}>
                    <div className={classes.content}>
                        <Avatar className={classes.avatar} alt='Login icon'>
                            <NoEncryptionIcon className={classes.icon} />
                        </Avatar>
                        <Typography variant="h5" >{isLoginPage
                            ? t('authentication.loginPageMessage')
                            : t('authentication.needToLogin')
                        }</Typography>
                        <ButtonBase className={providerType === 'auth0' ? classes.auth0Button : classes.googleButton}
                            onClick={logIn}>
                            <Grid container justifyContent='flex-start' alignItems='center' wrap='nowrap'>
                                {providerType === 'auth0'
                                    ? <ItsMeLogo style={{ height: 40, width: 60 }} />
                                    : <GoogleLogo style={{ height: 20, width: 60 }} />
                                }
                                <Typography variant='h6'>
                                    {t('authentication.login', { provider: providerType === 'auth0' ? 'itsme' : 'Google' })}
                                </Typography>
                            </Grid>
                        </ButtonBase>
                    </div>
                </div>
            </Stack>
        </LogoContainer>
    )
}

export default Login